// Copyright (c) StrangeBee 2024

import { ExtractProps } from '@Molecules/Details/Details';
import { TaskDisplayStatus, TaskStatusElements } from '@Organisms/Task/TaskDisplayStatus/TaskDisplayStatus';
import { TaskEventList } from '@Organisms/Task/eventList';
import React, { ReactElement } from 'react';
import { TaskOutput, TaskStatus, UserOutput, SharedOrgOutput, ActionOutput } from 'thehive-sdk';
import TaskLogList from '@Organisms/TaskLog/TaskLogList/TaskLogList';
import { TitleSection } from '@Atoms/TitleSection/TitleSection';
import { EntitySharingCreate } from '@Organisms/Shares/EntitySharingCreate/EntitySharingCreate';
import { TaskLogManage } from '@Organisms/TaskLog/TaskLogManage/TaskLogManage';
import { AddItemButton } from '@Atoms/Buttons';
import { TaskLogEventList } from '@Organisms/TaskLog/eventList';
import { DisplayEmptyData } from '@Atoms/Display/DisplayEmptyData/DisplayEmptyData';
import { PaginationSize } from '@Atoms/Inputs/PaginationSize/PaginationSize';
import { ListItemsPerPageType } from '@Constants/pagination';
import { THDatePicker } from '@Molecules/Inputs/THDatePicker/THDatePicker';
import { THForm } from '@Molecules/THForm/THForm';
import { THSelect } from '@Molecules/Inputs/THSelect/THSelect';
import { THAutoComplete } from '@Molecules/Inputs/THAutoComplete/THAutoComplete';
import { THSwitch } from '@Molecules/Inputs/THSwitch/THSwitch';
import moment from 'moment';
import { classList } from '@Helpers/className';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import { THMarkdown } from '@Molecules/Inputs/THMarkdown/THMarkdown';
import { THInput } from '@Molecules/Inputs/THInput/THInput';
import { normalizeDate } from '@Helpers/date';
import ResponderReports from '@Organisms/Report/ResponderReports/ResponderReports';
import { onCaseImageUpload } from '@Helpers/download';
import { THUserSelector } from '@Molecules/Inputs/THUserSelector/THUserSelector';
import { EntitySharingList } from '@Organisms/Shares/EntitySharingList/EntitySharingList';
import { RequireActionButton } from './RequireActionButton/RequireActionButton';
import { columnsRenderer } from '@Hooks/useConfigList/helper/computeHeader';

import './extractData.scss';
import { RangePickerProps } from 'antd/lib/date-picker';
// import { RangePickerProps } from 'antd/lib/date-picker/generatePicker';

interface ExtractDataProps {
    data: TaskOutput;
    eventList: TaskEventList;
    taskLogEventList: TaskLogEventList;
    locale: (label: string) => string;
    hasPermissions: boolean;
    hasSharePermissions: boolean;
    assignableUsers?: UserOutput[];
    refetchAssignableUsers: () => void;
    shareList: SharedOrgOutput[];
    sharedOrgList: SharedOrgOutput[];
    organisation: string;
    tasksList?: TaskOutput[];
    caseId: string;
    hasCortex?: boolean;
    formEditMode?: boolean;
}

export const extractData = ({
    data,
    eventList,
    taskLogEventList,
    locale,
    hasPermissions,
    hasSharePermissions,
    assignableUsers,
    refetchAssignableUsers,
    shareList,
    sharedOrgList = [],
    organisation,
    tasksList,
    caseId,
    hasCortex = false,
    formEditMode,
}: ExtractDataProps): Record<string, ExtractProps> => ({
    title: {
        className: 'task-details__section',
        render: () => (
            <THForm.Item name="title" label={locale('common.title')} rules={[{ required: true }]}>
                <THInput borderless disabled={!hasPermissions} />
            </THForm.Item>
        ),
    },
    flag: {
        className: 'task-details__flag',
        render: (): ReactElement => (
            <THForm.Item name="flag" valuePropName="checked" label={locale('task.details.flag')}>
                <THSwitch disabled={!hasPermissions} />
            </THForm.Item>
        ),
    },
    status: {
        className: 'task-details__status',
        render: (): ReactElement => (
            <THForm.Item name="status" label={locale('task.details.status')}>
                <THSelect
                    borderless
                    disabled={!hasPermissions}
                    options={Object.entries(TaskStatusElements).map(([key]) => ({
                        label: <TaskDisplayStatus key={key} status={key as any} displayLabel />,
                        value: key,
                    }))}
                />
            </THForm.Item>
        ),
    },
    '#statusMandatory': {
        className: 'task-details__status',
        render: ({ valid = true }): ReactElement => (
            <THForm.Item name="status" label={locale('task.details.status')}>
                <THSelect borderless disabled={!hasPermissions}>
                    {Object.entries(TaskStatusElements).map(([key]) => {
                        const disabled = !valid && key === TaskStatus.COMPLETED;
                        return (
                            <THSelect.Option key={key} value={key} disabled={disabled}>
                                <TaskDisplayStatus
                                    status={key as any}
                                    displayLabel
                                    {...(disabled ? { help: locale('task.mandatory.info.status') } : {})}
                                />
                            </THSelect.Option>
                        );
                    })}
                </THSelect>
            </THForm.Item>
        ),
    },
    group: {
        className: 'task-details__group',
        isForce: true,
        render: (): ReactElement => (
            <THForm.Item name="group" label={locale('task.details.group')} rules={[{ required: true }]}>
                <THAutoComplete
                    borderless
                    placeholder={locale('task.details.group')}
                    disabled={!hasPermissions}
                    options={tasksList
                        ?.reduce((acc, { group }) => (acc.includes(group) ? acc : [...acc, group]), [] as string[])
                        .map((value) => ({ label: value, value }))}
                />
            </THForm.Item>
        ),
    },
    assignee: {
        className: 'task-details__assignee',
        isForce: true,
        render: (): ReactElement => (
            <THForm.Item name="assignee" label={locale('common.assignee')}>
                <THUserSelector
                    allowClear={false}
                    showSearch={true}
                    users={assignableUsers}
                    disabled={!hasPermissions}
                    onDropdownVisibleChange={(open) => open && refetchAssignableUsers()}
                />
            </THForm.Item>
        ),
    },
    startDate: {
        isForce: true,
        render: (): ReactElement => (
            <THForm.Item<any> noStyle shouldUpdate={(prev, next) => prev.endDate !== next.endDate}>
                {({ getFieldsValue }) => {
                    const { endDate } = getFieldsValue();

                    const disableDate: RangePickerProps['disabledDate'] = (current) =>
                        endDate ? current && current > moment(endDate) : false;

                    return (
                        <THForm.Item name="startDate" label={locale('common.start-date')} normalize={normalizeDate}>
                            <THDatePicker
                                borderless
                                disabled={!hasPermissions}
                                disabledDate={disableDate}
                                showNow={endDate ? moment(Date.now()) < moment(endDate) : true}
                            />
                        </THForm.Item>
                    );
                }}
            </THForm.Item>
        ),
    },
    endDate: {
        render: (): ReactElement => (
            <THForm.Item<any> noStyle shouldUpdate={(prev, next) => prev.endDate !== next.endDate}>
                {({ getFieldsValue }) => {
                    const { startDate } = getFieldsValue();

                    const disableDate: RangePickerProps['disabledDate'] = (current) =>
                        startDate ? current && current < moment(startDate) : false;

                    return (
                        <THForm.Item name="endDate" label={locale('common.end-date')} normalize={normalizeDate}>
                            <THDatePicker
                                borderless
                                disabled={!hasPermissions}
                                disabledDate={disableDate}
                                showNow={startDate ? moment(Date.now()) > moment(startDate) : true}
                            />
                        </THForm.Item>
                    );
                }}
            </THForm.Item>
        ),
    },
    dueDate: {
        render: (): ReactElement => (
            <THForm.Item<any> noStyle shouldUpdate={(prev, next) => prev.dueDate !== next.dueDate}>
                {({ getFieldValue }) => (
                    <THForm.Item name="dueDate" label={locale('common.due-date')} normalize={normalizeDate}>
                        <THDatePicker
                            borderless
                            placeholder={locale('common.due-date')}
                            disabled={!hasPermissions}
                            className={classList(
                                getFieldValue('dueDate') - moment.now() > 0 ? 'before' : 'after',
                                'task-details__due-date',
                            )}
                        />
                    </THForm.Item>
                )}
            </THForm.Item>
        ),
        isForce: true,
    },
    mandatory: {
        className: 'task-details__mandatory',
        render: (): ReactElement => (
            <THForm.Item name="mandatory" valuePropName="checked" label={locale('task.details.mandatory')}>
                <THSwitch disabled={!hasPermissions} />
            </THForm.Item>
        ),
    },
    description: {
        className: 'task-details__description',
        isForce: true,
        render: () => (
            <THForm.Item name="description" label={locale('task.details.description')}>
                <THMarkdown
                    borderless
                    disabled={!hasPermissions}
                    onImageUpload={onCaseImageUpload(caseId)}
                    formEditMode={formEditMode}
                />
            </THForm.Item>
        ),
    },
    '#computeLogs': {
        name: ({ paginationManager: { itemsPerPage, setPagination }, count, setVisible }: any): ReactElement => (
            <TitleSection
                extraAction={
                    <PaginationSize
                        paginationConfig={{
                            itemsPerPage,
                            onItemsPerPageChange: (itemsPerPage: ListItemsPerPageType): void =>
                                setPagination?.({ itemsPerPage, page: 0 }),
                        }}
                    />
                }
            >
                <span className={classList('task-log-list__title')}>
                    {locale('task.logs.title')}
                    {count ? <span> ({count})</span> : null}
                </span>
                {hasPermissions && (
                    <TaskLogManage
                        task={data}
                        onVisibleChange={setVisible}
                        onTaskClose={() => eventList.onEdit({ status: TaskStatus.COMPLETED }, data._id)}
                        triggerElem={<AddItemButton />}
                        onFormFinish={(taskLog): Promise<any> => taskLogEventList.onCreate(taskLog, data._id)}
                    />
                )}
            </TitleSection>
        ),
        className: 'task-details__logs',
        isForce: true,
        render: ({ list, count, paginationManager, setVisible }: any): ReactElement =>
            !!count ? (
                <TaskLogList
                    taskEventList={eventList}
                    taskId={data._id}
                    list={list}
                    count={count}
                    paginationManager={paginationManager}
                    eventList={taskLogEventList}
                    hasCortex={hasCortex}
                    caseId={caseId}
                    task={data}
                />
            ) : (
                <DisplayEmptyData label={locale('task-log.none')}>
                    {hasPermissions && (
                        <TaskLogManage
                            task={data}
                            onVisibleChange={setVisible}
                            onTaskClose={() => eventList.onEdit({ status: TaskStatus.COMPLETED }, data._id)}
                            triggerElem={<THButton type="link">{locale('task-log.create.title')}</THButton>}
                            onFormFinish={(taskLog): Promise<any> => taskLogEventList.onCreate(taskLog, data._id)}
                        />
                    )}
                </DisplayEmptyData>
            ),
    },
    '#computeResponderReportList': {
        name: <TitleSection>{locale('case.description.sider.responder-reports.title')}</TitleSection>,
        isForce: true,
        className: 'task-details__reports',
        render: (reports: ActionOutput[]): ReactElement =>
            !!reports.length ? (
                <ResponderReports reports={reports} columnsRenderer={columnsRenderer} />
            ) : (
                <DisplayEmptyData label={locale('case.description.sider.responder-reports.no-reports')} />
            ),
    },
    '#computeShares': {
        name: ({ setVisible }): ReactElement => (
            <TitleSection>
                <div>{locale('sharing.title')}</div>
                {hasSharePermissions && eventList.shares.onAdd && eventList.shares.onCaseShareList && (
                    <EntitySharingCreate
                        entityId={data._id}
                        onAdd={eventList.shares.onAdd}
                        onCaseShareList={eventList.shares.onCaseShareList}
                        currentOrganisation={organisation}
                        alreadySharedList={shareList}
                        caseId={caseId}
                        onVisibleChange={setVisible}
                    />
                )}
            </TitleSection>
        ),
        className: 'task-details__shares',
        isForce: true,
        hidden: !sharedOrgList?.length,
        render: ({ shareList = [] }: { shareList: SharedOrgOutput[] }): ReactElement | null =>
            !!shareList.length ? (
                <EntitySharingList
                    caseId={caseId}
                    entityId={data._id}
                    eventList={eventList.shares}
                    shareList={shareList}
                    hasPermissions={hasSharePermissions}
                    extraAction={(organisationName): ReactElement => (
                        <RequireActionButton
                            task={data}
                            actionRequired={!!data?.extraData?.actionRequiredMap?.[organisationName]}
                            organisationName={organisationName}
                            eventList={eventList}
                            taskId={data._id}
                            label={{ requireAction: 'Require Action', cancelAction: 'Cancel Request' }}
                        />
                    )}
                />
            ) : (
                <DisplayEmptyData label={locale('sharing.no-shares')}>
                    {eventList.shares.onAdd && eventList.shares.onCaseShareList && (
                        <EntitySharingCreate
                            entityId={data._id}
                            onAdd={eventList.shares.onAdd}
                            onCaseShareList={eventList.shares.onCaseShareList}
                            currentOrganisation={organisation}
                            alreadySharedList={shareList}
                            caseId={caseId}
                            triggerElem={<THButton type="link">{locale('sharing.add')}</THButton>}
                        />
                    )}
                </DisplayEmptyData>
            ),
    },
});
